import React from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BuildIcon from "@mui/icons-material/Build";
import BusinessIcon from "@mui/icons-material/Business";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import DevicesIcon from "@mui/icons-material/Devices";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import SchemaOutlinedIcon from "@mui/icons-material/SchemaOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SubscriptionsOutlinedIcon from "@mui/icons-material/SubscriptionsOutlined";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";

import { SOFTWARE_VENDOR_TYPE } from "config/appMode";

// Same gating contract as environmentalNavigation.js. "ViewAsset" is the
// baseline "has portal access" action.
//
// Sidebar items target the existing flex2 routes registered in src/routes.js
// — the per-page Fixed Ops surfaces (a dedicated work-orders list under
// /fixedops/work-orders, etc.) land in follow-up PRs. Items without any
// flex2 equivalent today (Approvals Inbox; Approval Rules/Gates/Notifications)
// are deliberately omitted rather than left as dead placeholders.
const primaryRoutes = [
  {
    // Fixed Ops Control Center — the org's own service operations.
    // noCollapse:true → clickable header that always shows its children.
    type: "collapse",
    name: "Fixed Ops Control Center",
    key: "fixedops-nav-control-center",
    route: "/fixedops/control-center",
    noCollapse: true,
    icon: <BuildIcon fontSize="small" />,
    collapse: [
      {
        name: "My Work Orders",
        key: "fixedops-nav-cc-work-orders",
        route: "/work-orders",
        icon: <AssignmentIcon fontSize="small" />,
        requiredAction: "ViewAsset",
      },
      {
        name: "Tickets",
        key: "fixedops-nav-cc-tickets",
        route: "/tickets",
        icon: <ConfirmationNumberIcon fontSize="small" />,
        requiredAction: "ViewAsset",
      },
      {
        // Payables — invoices sent TO this org. The /payables page is the
        // same directional view AFMS uses under its EEC.
        name: "Payables",
        key: "fixedops-nav-cc-payables",
        route: "/payables",
        icon: <RequestQuoteOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
      },
    ],
  },
  {
    // Fixed Ops Command Center — vendor-facing. Hidden via requiresVendorOrg.
    type: "collapse",
    name: "Fixed Ops Command Center",
    key: "fixedops-nav-command-center",
    route: "/fixedops/command-center",
    noCollapse: true,
    icon: <HomeOutlinedIcon fontSize="small" />,
    requiredAction: "ViewAsset",
    requiresVendorOrg: true,
    collapse: [
      {
        name: "Client Assets",
        key: "fixedops-nav-cmd-client-assets",
        route: "/vendor/devices",
        icon: <DevicesIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresVendorOrg: true,
      },
      {
        // Invoices this org has issued. Points at the Fixed Ops surface rather
        // than the older /receivables listing — same data, but with view /
        // edit-while-draft / download / send / mark-paid instead of just
        // download and mark-paid. /receivables is still routed for bookmarks.
        name: "Invoices",
        key: "fixedops-nav-cmd-invoices",
        route: "/fixedops/invoices",
        icon: <ReceiptLongOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresVendorOrg: true,
      },
      {
        // Service Catalog routes to /servive-parts — note the legacy typo in
        // the existing route key (matches what's registered in routes.js).
        name: "Service Catalog",
        key: "fixedops-nav-cmd-service-catalog",
        route: "/servive-parts",
        icon: <MenuBookOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresVendorOrg: true,
      },
      {
        // Inventory — a collapse group, not a leaf. Children cover the item
        // catalogue (Items), the location hierarchy (Inventory Locations),
        // and the on-hand quantity view (Inventory). It sits under Service
        // Catalog because it's the stock a vendor sells FROM, not inbound
        // service ops. The direct /inventory-locations leaf that used to
        // live here is gone — the products page is still reachable from its
        // own surface but isn't the Fixed Ops menu's job anymore.
        type: "collapse",
        name: "Inventory",
        key: "fixedops-nav-cmd-inventory",
        icon: <Inventory2OutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresVendorOrg: true,
        collapse: [
          {
            name: "Items",
            key: "fixedops-nav-cmd-inventory-items",
            route: "/fixedops/inventory/items",
            icon: <WidgetsOutlinedIcon fontSize="small" />,
            requiredAction: "ViewAsset",
            requiresVendorOrg: true,
          },
          {
            name: "Inventory Locations",
            key: "fixedops-nav-cmd-inventory-locations",
            route: "/fixedops/inventory/locations",
            icon: <PlaceOutlinedIcon fontSize="small" />,
            requiredAction: "ViewAsset",
            requiresVendorOrg: true,
          },
          {
            name: "Inventory",
            key: "fixedops-nav-cmd-inventory-overview",
            route: "/fixedops/inventory/inventory",
            icon: <WarehouseOutlinedIcon fontSize="small" />,
            requiredAction: "ViewAsset",
            requiresVendorOrg: true,
          },
        ],
      },
      {
        // Software Catalog — what the platform itself is sold as. Only the
        // org carrying the "Software & Subscriptions" vendor type (F2 today)
        // sees it; every other vendor org gets the two catalogs above and
        // nothing here.
        type: "collapse",
        name: "Software Catalog",
        key: "fixedops-nav-cmd-software-catalog",
        icon: <TerminalOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresVendorOrg: true,
        requiresVendorType: SOFTWARE_VENDOR_TYPE,
        collapse: [
          {
            name: "Subscriptions",
            key: "fixedops-nav-cmd-software-subscriptions",
            route: "/fixedops/software-catalog/subscriptions",
            icon: <SubscriptionsOutlinedIcon fontSize="small" />,
            requiredAction: "ViewAsset",
            requiresVendorOrg: true,
            requiresVendorType: SOFTWARE_VENDOR_TYPE,
          },
        ],
      },
      {
        // Quotations lists the quotes this org has ISSUED
        // (AdminQuotationApiService.getQuotations) — outbound work, so it
        // belongs beside Receivables rather than in the Control Center.
        // Clients reach quotes they received through the emailed
        // /user/quotations/:id/details + checkout links.
        name: "Quotations",
        key: "fixedops-nav-cmd-quotations",
        route: "/user/quotations/listing",
        icon: <RequestQuoteOutlinedIcon fontSize="small" />,
        requiredAction: "ViewAsset",
        requiresVendorOrg: true,
      },
    ],
  },
];

const settingsGroup = {
  type: "collapse",
  name: "Settings",
  key: "fixedops-nav-settings",
  icon: <SettingsOutlinedIcon fontSize="small" />,
  collapse: [
    {
      name: "Organization",
      key: "fixedops-nav-settings-organization",
      route: "/organization-admin",
      icon: <BusinessIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      name: "Users",
      key: "fixedops-nav-settings-users",
      route: "/users/listing",
      icon: <PeopleAltIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      name: "Assets",
      key: "fixedops-nav-settings-assets",
      route: "/assets/listings",
      icon: <PrecisionManufacturingOutlinedIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      name: "Devices",
      key: "fixedops-nav-settings-devices",
      route: "/user/devices",
      icon: <DevicesIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      name: "Parts",
      key: "fixedops-nav-settings-parts",
      route: "/parts/view",
      icon: <Inventory2OutlinedIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      // BOM Templates currently shares the Service Catalog target — they're
      // the same ServicePart/ProductPart master records today. Split into a
      // dedicated route when the templates surface diverges.
      name: "BOM Templates",
      key: "fixedops-nav-settings-bom-templates",
      route: "/servive-parts",
      icon: <MenuBookOutlinedIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
    {
      // Workflows — read-only listing of workflow-api definitions active for
      // this org. Phase A surface; editor lands when workflow-api v2 ships.
      name: "Workflows",
      key: "fixedops-nav-settings-workflows",
      route: "/fixedops/settings/workflows",
      icon: <SchemaOutlinedIcon fontSize="small" />,
      requiredAction: "ViewAsset",
    },
  ],
};

const fixedOpsPortalRoutes = [...primaryRoutes, settingsGroup];

export const itemPassesGate = (route, allowedActions, isPlatformAdmin, orgContext = {}) => {
  if (route.requiresVendorOrg && orgContext.isVendorOrg !== true) return false;
  // Vendor-type gate. Unlike requiredAction this does NOT fail open while
  // capabilities load — vendorTypes comes straight off userDetails, so an
  // empty list means "this org genuinely has none", not "not resolved yet".
  if (route.requiresVendorType &&
      !(orgContext.vendorTypes || []).includes(route.requiresVendorType)) return false;
  if (route.requiresPlatformAdmin) return isPlatformAdmin === true;
  if (route.requiredAction) return allowedActions.has(route.requiredAction);
  return true;
};

const filterRoutesByCapabilities = (routes, allowedActions, isPlatformAdmin, orgContext) =>
  routes
    .map((route) => {
      if (route.type === "title" || route.type === "divider") return route;
      if (!itemPassesGate(route, allowedActions, isPlatformAdmin, orgContext)) return null;
      if (!route.collapse) return route;
      const collapse = filterRoutesByCapabilities(
        route.collapse,
        allowedActions,
        isPlatformAdmin,
        orgContext
      );
      if (route.type === "collapse" && collapse.length === 0 && !route.route) return null;
      return { ...route, collapse };
    })
    .filter(Boolean);

const stripOrphanTitles = (routes) =>
  routes.filter((route, index) => {
    if (route.type !== "title") return true;
    for (let i = index + 1; i < routes.length; i += 1) {
      if (routes[i].type === "title") return false;
      if (routes[i].type !== "divider") return true;
    }
    return false;
  });

/**
 * Builds the Fixed Ops portal sidebar, gated against the caller's portal
 * capabilities + org context. Mirrors getEnvironmentalPortalRoutes. When
 * capabilities haven't resolved yet, every cap-gated item shows so the
 * sidebar isn't blank during the first paint — org-context gates
 * (requiresVendorOrg) still apply.
 */
export const getFixedOpsPortalRoutes = (capabilities, orgContext = {}) => {
  if (!capabilities || !capabilities.portalRole) {
    const openAllActions = { has: () => true };
    return stripOrphanTitles(
      filterRoutesByCapabilities(fixedOpsPortalRoutes, openAllActions, true, orgContext)
    );
  }
  const allowedActions = new Set(capabilities.allowedActions || []);
  const isPlatformAdmin = capabilities.isPlatformAdmin === true;
  return stripOrphanTitles(
    filterRoutesByCapabilities(fixedOpsPortalRoutes, allowedActions, isPlatformAdmin, orgContext)
  );
};

export default fixedOpsPortalRoutes;
